import { ContentCopy, EmailOutlined, OpenInNew, WhatsApp } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import useKothar from "context/useKothar";
import usePrEnquiryShareLink from "hooks/usePrEnquiryShareLink";
import QRCode from "qrcode";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { buildClientDetailsUrl } from "utils/clientDetailsFormConfig";

// The client details form hangs off the organization's check-in slug; the PR enquiry form has
// its own share link.
const FORMS = {
  details: {
    label: "Client details",
    settingsTab: "Client Details Form",
    file: "client-details-form-qr.png",
    caption: "Clients can scan this code or open the link to fill in their details.",
    subject: "Your client details form",
    message: (url) => `Hi, please fill in your details using this secure form: ${url}`,
  },
  pr: {
    label: "PR enquiry",
    settingsTab: "PR Enquiry Form",
    file: "pr-enquiry-form-qr.png",
    caption:
      "Prospective clients share their visa, study, work and goals so you can plan their PR pathway.",
    subject: "Plan your PR pathway with us",
    message: (url) =>
      `Hi, to help us plan your PR pathway, please tell us about your visa, study, work and goals here (about 5 minutes): ${url}`,
  },
};

/** `initialForm` picks which form opens first: "details" or "pr". */
const ClientDetailsShareModal = ({ open, onClose, initialForm = "details" }) => {
  const [{ publicUrl }] = useKothar();
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [formKind, setFormKind] = useState(initialForm);
  const kind = FORMS[formKind] ? formKind : "details";
  const form = FORMS[kind];
  const prShare = usePrEnquiryShareLink({ enabled: open && kind === "pr" });

  useEffect(() => {
    if (open) setFormKind(initialForm);
  }, [open, initialForm]);

  const detailsUrl = useMemo(
    () => (kind === "pr" ? prShare.url : buildClientDetailsUrl(publicUrl)),
    [kind, prShare.url, publicUrl]
  );

  useEffect(() => {
    let cancelled = false;
    const generate = async () => {
      if (!detailsUrl) {
        if (!cancelled) setQrDataUrl("");
        return;
      }
      try {
        const data = await QRCode.toDataURL(detailsUrl, {
          width: 256,
          margin: 2,
          color: { dark: "#000000", light: "#FFFFFF" },
        });
        if (!cancelled) setQrDataUrl(data);
      } catch (error) {
        if (!cancelled) {
          setQrDataUrl("");
          toast.error("Failed to generate QR code");
        }
      }
    };
    if (open) generate();
    return () => {
      cancelled = true;
    };
  }, [open, detailsUrl]);

  const handleCopy = async () => {
    if (!detailsUrl) {
      toast.error("No URL to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(detailsUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const link = document.createElement("a");
    link.download = form.file;
    link.href = qrDataUrl;
    link.click();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share a client form</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={formKind}
            onChange={(_, next) => next && setFormKind(next)}
            aria-label="Which form to share"
          >
            {Object.entries(FORMS).map(([key, item]) => (
              <ToggleButton key={key} value={key} sx={{ textTransform: "none", px: 2 }}>
                {item.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {kind === "pr" && prShare.isLoading ? null : !detailsUrl ? (
            <Typography variant="body2" color="text.secondary">
              Generate the public link first from <strong>Settings → {form.settingsTab}</strong>,
              then come back here to share it.
            </Typography>
          ) : (
            <>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Form URL
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    backgroundColor: "var(--app-surface-muted)",
                    borderRadius: 1,
                    border: "1px solid #ddd",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ flexGrow: 1, fontFamily: "monospace", wordBreak: "break-all" }}
                  >
                    {detailsUrl}
                  </Typography>
                  <Tooltip title="Open in new tab">
                    <IconButton
                      size="small"
                      onClick={() => window.open(detailsUrl, "_blank", "noopener,noreferrer")}
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy link">
                    <IconButton size="small" onClick={handleCopy}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box className="flex justify-center">
                {qrDataUrl ? (
                  <img
                    src={qrDataUrl}
                    alt={`${form.label} form QR code`}
                    style={{ maxWidth: "100%", height: "auto" }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Generating QR…
                  </Typography>
                )}
              </Box>

              <Typography variant="caption" color="text.secondary" align="center">
                {form.caption}
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button
          startIcon={<WhatsApp />}
          disabled={!detailsUrl}
          href={`https://wa.me/?text=${encodeURIComponent(form.message(detailsUrl))}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Send on WhatsApp
        </Button>
        <Button
          startIcon={<EmailOutlined />}
          disabled={!detailsUrl}
          href={`https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(
            form.subject
          )}&body=${encodeURIComponent(form.message(detailsUrl))}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Send by email
        </Button>
        <span style={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handleDownload} variant="contained" disabled={!qrDataUrl}>
          Download QR
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientDetailsShareModal;
